/**
 * Phase 101 — Digital Letterhead shell (print-ready container).
 *
 * Pure presentational. Caller supplies academy metadata + the body content.
 * The print CSS is local to this component (style tag in render) — it sizes
 * to A4, sets 1-inch margins, hides UI chrome, and centers the page number.
 *
 * Usage:
 *   <LetterheadShell academy={academy} signingAuthority={authority} subject={subject} addressedTo={to} dateLabel={date}>
 *     {bodyText.split('\n').map((para, i) => <p key={i}>{para}</p>)}
 *   </LetterheadShell>
 */
import React from 'react';

const A4_PAGE_CSS = `
@media print {
  @page { size: A4; margin: 1in; }

  /* Phase 121C — Reset the page chrome so the printable flows from the top of
     the page without inheriting any dashboard / dialog padding or scroll. */
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  /* Phase 121C — CRITICAL: neutralize Radix Dialog containing-block trap.
     <DialogContent> uses transform:translate(-50%,-50%) + max-h:90vh + overflow:auto,
     which (a) becomes the containing block for absolute children and (b) clips
     anything beyond the viewport.  Both of those silently truncated the print
     output to roughly one screenful.  Reset every ancestor that could clip. */
  [data-radix-dialog-overlay],
  [data-radix-dialog-content],
  [role="dialog"],
  [data-state="open"][role="dialog"] {
    position: static !important;
    transform: none !important;
    inset: auto !important;
    top: auto !important; left: auto !important; right: auto !important; bottom: auto !important;
    max-height: none !important;
    height: auto !important;
    max-width: none !important;
    overflow: visible !important;
    background: #ffffff !important;
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  /* Hide non-printable chrome by removing it from layout flow entirely.  Using
     display:none on the ancestors of .letterhead-printable would also hide the
     letterhead itself, so we walk only siblings via the visibility trick — but
     then explicitly force layout-flow visibility on the printable subtree. */
  body * { visibility: hidden !important; }
  .letterhead-printable, .letterhead-printable * { visibility: visible !important; }

  /* The printable now flows naturally as the only visible block on the page.
     No position:absolute / inset:0 (those re-introduced the trap on Radix). */
  .letterhead-printable {
    display: block !important;
    position: static !important;
    width: 100% !important;
    max-width: none !important;
    height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    border: none !important;
    background: #ffffff !important;
    overflow: visible !important;
  }
  .letterhead-printable * { overflow: visible !important; }

  /* Phase 247.d — Force colored backgrounds/borders to render even when the
     user UNCHECKS "Background graphics" in Chrome's print options (the
     default for many ops users on macOS). Without this, the academy header
     band, the dotted body border, and any colored accent strips disappear
     in the printed PDF / paper, leaving the letter looking like a Word doc
     instead of a branded letterhead. */
  *, *::before, *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .no-print { display: none !important; }
}
.letterhead-printable {
  font-family: Georgia, "Times New Roman", serif;
  color: #0f172a;
  line-height: 1.6;
  font-size: 13.5px;
}
.letterhead-printable .lh-header {
  display: grid; grid-template-columns: 80px 1fr; gap: 16px; align-items: center;
  padding-bottom: 16px; border-bottom: 2px solid #0f172a;
}
.letterhead-printable .lh-logo { width: 80px; height: 80px; object-fit: contain; }
.letterhead-printable .lh-academy-name { font-weight: 700; font-size: 22px; color: #0f172a; letter-spacing: 0.4px; }
.letterhead-printable .lh-academy-meta { font-size: 11px; color: #475569; line-height: 1.45; margin-top: 4px; }
.letterhead-printable .lh-body { padding: 28px 0; min-height: 480px; }
.letterhead-printable .lh-body p { margin: 0 0 12px 0; white-space: pre-wrap; }
.letterhead-printable .lh-footer {
  border-top: 1px solid #cbd5e1;
  padding-top: 10px; margin-top: 28px;
  display: flex; justify-content: space-between; align-items: center;
  font-size: 10px; color: #64748b; font-style: italic;
}
.letterhead-printable .lh-meta-row { display: flex; justify-content: space-between; font-size: 12px; color: #334155; margin-bottom: 14px; }
.letterhead-printable .lh-sign { margin-top: 36px; }
.letterhead-printable .lh-sign-line { width: 220px; border-top: 1px solid #475569; padding-top: 6px; }
`;

export function LetterheadShell({
  academy,
  subject,
  addressedTo,
  dateLabel,
  bodyText,
  signingAuthority,
  documentId,
  children,
}) {
  const aName = academy?.name || 'Academy';
  const aCity = academy?.city || '';
  const aPhone = academy?.phone || '';
  const aEmail = academy?.email || '';
  const aAddress = academy?.address || aCity;
  const aRegistration = academy?.registration_number || academy?.settings?.registration_number || '';
  const aGstin = academy?.gstin || academy?.settings?.gstin || '';
  const logoUrl = academy?.logo_url || academy?.settings?.logo_url || null;
  // Phase 247.d — Initials fallback for the print/preview path when a brand
  // logo isn't uploaded. The previous "Logo" placeholder string showed up as
  // literal text in the browser print preview, making the letter look like
  // a developer stub. Initials = first letter of up to two words from the
  // academy name (e.g., "Vijayawada Blues FC" → "VB", "Out-Play" → "OP",
  // "Academy" fallback → "A"). Rendered with a colored disk + tracking so
  // even monochrome printers still produce a recognizable mark.
  const aInitials = (aName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('') || aName.slice(0, 1)).toUpperCase();
  // Phase 247.e — Swap to the initials block if the remote logo URL fails to
  // resolve (CORS-stripped during print, 404, cold object-storage cache, etc.).
  // Without this fallback, the browser print driver renders a broken-image
  // glyph in the header band, which looks like a generic "LOGO Academy"
  // developer stub on the printed page. The `key={logoUrl}` forces a fresh
  // <img> mount when academy changes, which naturally resets the broken-state
  // without needing a useEffect (avoids set-state-in-effect lint warnings).
  const [logoBroken, setLogoBroken] = React.useState(false);
  const showLogoImage = !!logoUrl && !logoBroken;

  return (
    <div className="letterhead-printable bg-white p-12 shadow-md border border-slate-200" data-testid="letterhead-shell">
      <style>{A4_PAGE_CSS}</style>

      {/* Header */}
      <div className="lh-header">
        {showLogoImage ? (
          <img
            key={logoUrl}
            src={logoUrl}
            alt=""
            className="lh-logo"
            crossOrigin="anonymous"
            onError={() => setLogoBroken(true)}
          />
        ) : (
          <div
            className="lh-logo flex items-center justify-center rounded font-black text-white tracking-wider"
            style={{ background: '#0f172a', fontSize: '22px', letterSpacing: '0.08em' }}
            aria-label={`${aName} monogram`}
          >
            {aInitials}
          </div>
        )}
        <div>
          <div className="lh-academy-name">{aName}</div>
          <div className="lh-academy-meta">
            {aAddress && <div>{aAddress}</div>}
            <div>
              {aPhone && <span>Phone: {aPhone}</span>}
              {aPhone && aEmail && <span>  ·  </span>}
              {aEmail && <span>Email: {aEmail}</span>}
            </div>
            {(aRegistration || aGstin) && (
              <div className="opacity-80">
                {aRegistration && <span>Reg: {aRegistration}</span>}
                {aRegistration && aGstin && <span>  ·  </span>}
                {aGstin && <span>GSTIN: {aGstin}</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metadata strip */}
      <div className="lh-meta-row mt-5">
        <span>{documentId ? `Ref: ${documentId}` : ''}</span>
        <span style={{ marginLeft: '24px' }}>Date: {dateLabel || new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
      </div>

      {/* Body */}
      <div className="lh-body">
        {addressedTo && (
          <p style={{ whiteSpace: 'pre-line' }}><strong>To,</strong>{'\n'}{addressedTo}</p>
        )}
        {subject && (
          <p><strong>Subject:</strong> {subject}</p>
        )}
        {children ? children : (bodyText || '').split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)}
        {signingAuthority && (
          <div className="lh-sign">
            <div className="lh-sign-line">{signingAuthority}</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="lh-footer">
        <span>Building the Future of Sports</span>
        <span style={{ marginLeft: '24px' }}>Page 1 of 1</span>
      </div>
    </div>
  );
}

/**
 * Convenience helper: open print dialog for the currently-rendered letterhead.
 * Caller should ensure exactly one .letterhead-printable is in the DOM.
 */
export function printLetterhead() {
  window.print();
}
